/**
 * **Review & close** — `docs/visit-page.md` §16, Board 12 A (passing, 602) and
 * B (opened early, 472); on a phone Board 16's sheet from the bottom (§19.2).
 *
 * The office's, and it locks (do-not-substitute 12; Aaron: *“Ops reviews and
 * closes”*). The seven lines of §16.2 in their order — a failing one amber ⚠
 * with what it still needs and, where there is one, the button that clears it;
 * **Close the visit** grey until every line passes, with no override (D13).
 * Once they pass: the **Final numbers** (internal — absent for a role without
 * *Costs & margins*), **Tell <salesperson>** and **Draft the <kind> invoice**
 * (both on, D14), *A word on the visit*, and **Close the visit**, which posts,
 * locks and recomputes the job in one transaction (§16.4).
 *
 * `GET /visits/:id/close-check` draws it, every word and figure the server's;
 * `POST /visits/:id/close {tellSalesperson, draftInvoice, note}` closes.
 */
import { useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { CloseCheck, CloseLine, FinalNumbers, Delta, VisitRead, VisitTabKey } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'
import { useVisitTab, useVisitWrite } from './api.js'
import { VisitButton, VisitCard, VisitTile } from './sheet.js'
import { CheckMark } from './day-parts.js'
import { FIELD_BOX, FIELD_INPUT, FieldLabel, TickBox } from './fields.js'
import { InternalChip } from './internal-chip.js'
import { CLOSE, COMMON } from './copy.js'
import { dayText, hours1, money, percent, signed, signedMoney, timeIn } from './format.js'

export function ReviewCloseSheet({ visitId, read, phone: forced, onClose, onClosed }: {
  visitId: string
  read: VisitRead
  phone?: boolean
  onClose: () => void
  /** After **Close the visit** — the page has the closed visit's Overview already. */
  onClosed?: () => void
}) {
  const windowPhone = usePhone()
  const phone = forced ?? windowPhone
  const check = useVisitTab<CloseCheck>(visitId, '/close-check')
  const write = useVisitWrite(visitId)
  const data = check.data
  const [tell, setTell] = useState<boolean | null>(null)
  const [draft, setDraft] = useState<boolean | null>(null)
  const [word, setWord] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [, setParams] = useSearchParams()
  const tz = read.timeZone

  const goTo = (tab: VisitTabKey, extra: Record<string, string> = {}) => {
    onClose()
    setParams((was) => {
      const next = new URLSearchParams(was)
      next.set('tab', tab)
      for (const [key, value] of Object.entries(extra)) next.set(key, value)
      return next
    })
  }

  const passes = data?.passes ?? false
  const telling = tell ?? data?.tellSalesperson?.default ?? false
  const drafting = draft ?? data?.draftInvoice?.default ?? false

  async function closeVisit() {
    if (!data?.passes || busy) return
    setBusy(true)
    setError(null)
    try {
      await write('POST', '/close', {
        tellSalesperson: Boolean(data.tellSalesperson) && telling,
        draftInvoice: Boolean(data.draftInvoice) && drafting,
        note: word.trim(),
      }, { tab: 'overview' })
      onClosed?.()
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const sub = !data ? undefined
    : data.progress?.kind === 'after'
      ? CLOSE.after(timeIn(tz, data.at, 'dateAmpm'), String(data.progress.index))
      : data.progress?.kind === 'day_of'
        ? CLOSE.during(timeIn(tz, data.at, 'dateAmpm'), String(data.progress.index), String(data.progress.count))
        : timeIn(tz, data.at, 'dateAmpm')

  const lead = !data ? null
    : data.passes ? CLOSE.passes
      : data.progress?.kind === 'day_of' && data.openCount === data.lines.length
        ? CLOSE.open(String(data.openCount), String(data.progress.index), String(data.progress.count))
        : null

  const footer = phone
    ? (
      <>
        <VisitButton size="phone" onClick={onClose}>{passes ? CLOSE.cancel : CLOSE.close}</VisitButton>
        <VisitButton size="phone" tone="primary" glyph="check" disabled={!passes || busy} onClick={() => void closeVisit()}>
          {CLOSE.submit}
        </VisitButton>
      </>
    )
    : passes
      ? (
        <>
          <VisitButton size="card" onClick={onClose}>{CLOSE.cancel}</VisitButton>
          <VisitButton size="card" tone="primary" glyph="check" disabled={busy} onClick={() => void closeVisit()}>
            {CLOSE.submit}
          </VisitButton>
        </>
      )
      : (
        <>
          <VisitButton size="popup" onClick={onClose}>{CLOSE.close}</VisitButton>
          <VisitButton size="popup" disabled>{CLOSE.submit}</VisitButton>
        </>
      )

  return (
    <VisitCard
      width={passes ? 602 : 472}
      phone={phone}
      title={CLOSE.title(read.reference)}
      sub={sub}
      mark={<VisitTile glyph="check" tone={passes ? 'accent' : 'grey'} size={36} />}
      onClose={onClose}
      footer={footer}
      note={passes ? CLOSE.footer(read.header.meta.job.reference) : CLOSE.footerOpen}
      asDrawn={passes ? 'vp-12 A' : 'vp-12 B'}
    >
      {data && (
        <div>
          {lead && <p className="mb-1 mt-0 text-[12.5px] text-text-muted">{lead}</p>}
          {data.lines.map((line) => (
            <CheckLine key={line.key} line={line} tz={tz} onGo={goTo} />
          ))}

          {passes && data.internal?.final && (
            <>
              <div className="h-3" />
              <FinalTable final={data.internal.final} />
            </>
          )}

          {passes && (data.tellSalesperson || data.draftInvoice) && (
            <>
              <div className="h-3" />
              <div className="flex flex-col gap-2">
                {data.tellSalesperson && (
                  <Option on={telling} onClick={() => setTell(!telling)}>
                    <b>{CLOSE.tell(data.tellSalesperson.person.name)}</b> {CLOSE.tellSub(data.tellSalesperson.workNoun)}
                  </Option>
                )}
                {data.draftInvoice && (
                  <Option on={drafting} onClick={() => setDraft(!drafting)}>
                    <b>{data.draftInvoice.kind === 'balance' ? CLOSE.draftBalance : CLOSE.draftKind(data.draftInvoice.kind)}</b>{' '}
                    {data.draftInvoice.deposit
                      ? CLOSE.draftBalanceSub(money(data.draftInvoice.price), data.draftInvoice.deposit.quoteReference,
                        dayText(data.draftInvoice.deposit.paidOn, 'monthDay'))
                      : CLOSE.draftSub(money(data.draftInvoice.price))}
                  </Option>
                )}
              </div>
            </>
          )}

          {passes && (
            <>
              <div className="h-2.5" />
              <div className="min-w-0">
                <FieldLabel htmlFor="close-word" optional={CLOSE.optional}>{CLOSE.aWord}</FieldLabel>
                <div className={FIELD_BOX}>
                  <input id="close-word" value={word} onChange={(event) => setWord(event.target.value)} className={cn(FIELD_INPUT, 'tabular-nums')} />
                </div>
              </div>
            </>
          )}

          {error && <p className="mb-0 mt-3 text-[12.5px] text-danger">{error}</p>}
        </div>
      )}
    </VisitCard>
  )
}

/** One of §16.2's seven lines — the mark, the title, what it says, and the button that clears it. */
function CheckLine({ line, tz, onGo }: {
  line: CloseLine
  tz: string
  onGo: (tab: VisitTabKey, extra?: Record<string, string>) => void
}) {
  const { detail, right } = describe(line, tz, onGo)
  return (
    <div className="flex items-center gap-[11px] border-t border-line-faint py-[9px]">
      <CheckMark pass={line.pass} />
      <div className="flex min-w-0 flex-1 flex-col">
        <b className="text-[13px] font-semibold">{CLOSE.lines[line.key]}</b>
        {detail && <span className="text-[11.5px] text-text-muted">{detail}</span>}
      </div>
      {right}
    </div>
  )
}

function rowButton(label: string, onClick: () => void): ReactNode {
  return <VisitButton size="row" onClick={onClick}>{label}</VisitButton>
}

/** What a line says, passing or open, in Board 12's words — every figure the server's. */
function describe(
  line: CloseLine,
  tz: string,
  onGo: (tab: VisitTabKey, extra?: Record<string, string>) => void,
): { detail: string | null; right?: ReactNode } {
  const d = CLOSE.detail
  switch (line.key) {
    case 'days':
      if (line.pass && line.last) {
        return {
          detail: d.daysDone(String(line.closedOut), String(line.total), String(line.last.index), line.last.by.name,
            timeIn(tz, line.last.at, 'dayAmpm')),
        }
      }
      if (line.onSiteDay !== null && line.tomorrowDay !== null) {
        return {
          detail: d.daysOpen(String(line.closedOut), String(line.total), String(line.onSiteDay),
            String(line.tomorrowDay), COMMON.tomorrow),
        }
      }
      return { detail: COMMON.of(String(line.closedOut), String(line.total)) }
    case 'time':
      if (line.pass) return { detail: d.timeDone(String(line.flags), String(line.edits)) }
      if (line.flagsOpen > 0) return { detail: null, right: rowButton(d.lookAtIt, () => onGo('time')) }
      return { detail: d.timeOpen(String(line.onTheClock), String(line.edits)) }
    case 'receipts': {
      if (line.pass) {
        const back = line.paidBack[0]
        return {
          detail: back
            ? d.receiptsDone(String(line.reviewed), back.vendor, timeIn(tz, back.approvedAt, 'day'), back.person.firstName)
            : d.reviewed(String(line.reviewed)),
        }
      }
      const first = line.toReview[0]
      if (!first) return { detail: null }
      return {
        detail: first.internal
          ? d.receiptsOpen(String(line.toReview.length), first.vendor, money(first.internal.amount))
          : d.receiptsOpenShort(String(line.toReview.length), first.vendor),
        right: rowButton(d.review, () => onGo('money', { receipt: first.id })),
      }
    }
    case 'rentals': {
      if (line.pass) {
        const last = [...line.rentals].reverse().find((rental) => rental.returnedAt)
        if (!last?.returnedAt) return { detail: null }
        const when = timeIn(tz, last.returnedAt, 'dayAmpm')
        return { detail: line.rentals.length === 2 ? d.rentalsDone(last.noun, when) : d.rentalReturned(last.noun, when) }
      }
      const parts = line.rentals
        .filter((rental) => rental.state !== 'cancelled')
        .map((rental) => (rental.state === 'returned'
          ? d.rentalBack(rental.noun)
          : rental.days[0] ? d.rentalBooked(rental.noun, dayText(rental.days[0], 'weekday')) : null))
        .filter((part): part is string => part !== null)
      return { detail: parts.length > 0 ? d.parts(parts) : null }
    }
    case 'leftovers': {
      if (line.pending) return { detail: d.leftoversOpen }
      const items = line.items.map((item) => d.leftover(
        String(item.qty),
        item.unit ? (item.qty === 1 ? item.unit.one : item.unit.many) : null,
        item.qty === 1 ? item.noun.one : item.noun.many,
      ))
      if (items.length === 0) return { detail: null }
      return { detail: line.internal ? d.leftoversDone(items, signedMoney(line.internal.off)) : COMMON.commaList(items) }
    }
    case 'equipment_hours':
      if (line.pass) return { detail: d.hoursDone }
      return {
        detail: d.hoursOpen(line.daysIn.map(String), line.daysToCome.map(String)),
      }
    case 'finished_photos':
      if (!line.pass) return { detail: line.count === 0 ? d.photosOpen : d.photosDone(String(line.count)) }
      return {
        detail: d.photosDone(String(line.count)),
        right: (
          <span className="flex items-center gap-[3px]">
            {line.thumbs.map((photo) => (
              <img key={photo.id} src={photo.file.url} alt={photo.note ?? photo.file.name}
                className="h-[26px] w-[34px] shrink-0 rounded-[5px] bg-line-soft object-cover" />
            ))}
          </span>
        ),
      }
  }
}

/** A signed figure in its tone: green under, amber over, a faint *—* for none (§3.2). */
function DeltaCell({ delta, text }: { delta: Delta; text: string }) {
  if (delta.tone === 'zero') return <span className="text-right text-text-faint">{'—'}</span>
  return (
    <span className={cn('whitespace-nowrap text-right font-bold', delta.tone === 'under' ? 'text-accent-dark' : 'text-warn')}>
      {text}
    </span>
  )
}

const FINAL_GRID = 'grid grid-cols-[1.5fr_104px_104px_104px] items-center gap-2.5 px-3'

/** **Final numbers** — internal: *Category · Estimate · Actual · vs estimate*, the total, the margin (§16.3). */
function FinalTable({ final }: { final: FinalNumbers }) {
  const head = CLOSE.final.head
  return (
    <div className="overflow-hidden rounded-[12px] border border-line bg-surface-internal">
      <div className="flex items-center gap-2 border-b border-line px-3 py-[9px]">
        <b className="text-[13px]">{CLOSE.final.title}</b>
        <InternalChip />
      </div>
      <div className={cn(FINAL_GRID, 'h-[31px] border-b border-line text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint')}>
        <span>{head.category}</span>
        <span className="text-right">{head.estimate}</span>
        <span className="text-right">{head.actual}</span>
        <span className="text-right">{head.vsEstimate}</span>
      </div>
      {final.rows.map((row) => (
        <div key={row.key} className={cn(FINAL_GRID, 'h-[37px] border-b border-line-faint text-[12.5px]')}>
          <span>
            {CLOSE.final.categories[row.key]}
            {row.hours && (
              <span className="text-[11px] text-text-muted">
                {' '}{CLOSE.final.laborHours(hours1(row.hours.estimated), hours1(row.hours.actual))}
              </span>
            )}
          </span>
          <span className="whitespace-nowrap text-right">{money(row.estimate)}</span>
          <span className="whitespace-nowrap text-right">{money(row.actual)}</span>
          <DeltaCell delta={row.delta} text={signedMoney(row.delta)} />
        </div>
      ))}
      <div className={cn(FINAL_GRID, 'h-[38px] bg-surface-internal-alt text-[13px]')}>
        <b>{CLOSE.final.totalCost}</b>
        <span className="whitespace-nowrap text-right font-bold">{money(final.total.estimate)}</span>
        <span className="whitespace-nowrap text-right font-bold">{money(final.total.actual)}</span>
        <DeltaCell delta={final.total.delta} text={signedMoney(final.total.delta)} />
      </div>
      <div className={cn(FINAL_GRID, 'h-9 text-[12.5px]')}>
        <span>
          {CLOSE.final.margin}{' '}
          <span className="text-text-muted">{CLOSE.final.on(money(final.margin.on))}</span>
        </span>
        <span className="whitespace-nowrap text-right text-[13px]">{percent(final.margin.estimate)}</span>
        <span className="whitespace-nowrap text-right text-[13px] font-bold">{percent(final.margin.actual)}</span>
        <DeltaCell delta={final.margin.delta} text={COMMON.pts(signed(final.margin.delta))} />
      </div>
    </div>
  )
}

/** **Tell Tom Reyes** *the install is done* — Board 12 A's 18px tick box, the whole row the target. */
function Option({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" role="checkbox" aria-checked={on} onClick={onClick} className="flex items-center gap-2.5 text-left">
      <TickBox on={on} />
      <span className="text-[12.5px]">{children}</span>
    </button>
  )
}
